import { ArtifactLogo } from "@/components/ui/artifact-logo";
import { AnimatedShinyText } from "@/components/ui/magicui/animated-shiny-text";
import { cn } from "@/lib/utils";
import { useThreadStore } from "@/store";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  CheckCircle,
  Globe,
  Loader2,
  Search,
  X,
} from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export function DeepResearch({
  isActive,
  onClose,
  isLoading: _isLoading,
  activity = [],
  sources = [],
  deepResearch: _deepResearch = true,
  status = "active",
  messageId,
  originalQueryMessageId,
}) {
  // Determine the correct message ID to use for looking up data
  const effectiveMessageId = originalQueryMessageId || messageId;

  const [activeTab, setActiveTab] = useState("activity");
  const location = useLocation();

  // Subscribe to current thread ID for thread-specific filtering
  const currentThreadId = useThreadStore((state) => state.currentThreadId);

  // Local state for live updates with sequential animation support
  const [liveActivity, setLiveActivity] = useState(activity);
  const [liveSources, setLiveSources] = useState(sources);
  const [liveStatus, setLiveStatus] = useState(status);

  // Ref to track if component is mounted and prevent memory leaks
  const isMountedRef = useRef(true);
  const _lastUpdateTimeRef = useRef(Date.now());

  // Track the thread ID this sidebar is associated with
  const [sidebarThreadId, setSidebarThreadId] = useState(null);

  // RACE CONDITION FIX: Close sidebar when navigating to different threads
  useEffect(() => {
    const pathThreadId = location.pathname.match(/\/chat\/([^/]+)/)?.[1];

    // When sidebar opens, track which thread it belongs to
    if (isActive && effectiveMessageId && !sidebarThreadId) {
      setSidebarThreadId(currentThreadId || pathThreadId);
    }

    // Close sidebar if user navigates to different thread
    if (isActive && sidebarThreadId && pathThreadId && pathThreadId !== "new") {
      if (pathThreadId !== sidebarThreadId) {
        if (onClose) onClose();
        setSidebarThreadId(null);
      }
    }

    // Clear thread ID when sidebar closes
    if (!isActive && sidebarThreadId) {
      setSidebarThreadId(null);
    }
  }, [
    isActive,
    location.pathname,
    effectiveMessageId,
    sidebarThreadId,
    onClose,
    currentThreadId,
  ]);

  // Listen for global sidebar close events
  useEffect(() => {
    const handleCloseAllSidebars = (_event) => {
      if (isActive && onClose) {
        onClose();
        setSidebarThreadId(null);
      }
    };

    window.addEventListener("close-all-sidebars", handleCloseAllSidebars);

    return () => {
      window.removeEventListener("close-all-sidebars", handleCloseAllSidebars);
    };
  }, [isActive, onClose]);

  // Create event handler for deep research updates with debouncing
  const handleDeepResearchUpdate = useCallback(
    (event) => {
      if (!isMountedRef.current) return;

      const {
        messageId: eventMessageId,
        data,
        threadId: eventThreadId,
      } = event.detail;
      const { currentThreadId } = useThreadStore.getState();

      // RACE CONDITION FIX: Only process events for messages in the current thread
      if (
        eventThreadId &&
        currentThreadId &&
        eventThreadId !== currentThreadId
      ) {
        return;
      }

      // Only process events for our specific message
      if (
        eventMessageId !== effectiveMessageId &&
        eventMessageId !== messageId
      ) {
        return;
      }

      // Additional safety check: if sidebar is for a specific thread, ensure event matches
      if (
        sidebarThreadId &&
        eventThreadId &&
        eventThreadId !== sidebarThreadId
      ) {
        return;
      }

      // Update status immediately
      if (data.status) {
        setLiveStatus(data.status);
      }

      // CRITICAL FIX: Handle full array updates with proper accumulation
      if (data.activity && Array.isArray(data.activity)) {
        // Always use the incoming full array since backend now sends accumulated data
        // Backend handles the accumulation, frontend just displays the complete list
        setLiveActivity(data.activity);
      }

      // CRITICAL FIX: Handle full sources array updates with proper accumulation
      if (data.sources && Array.isArray(data.sources)) {
        // Always use the incoming full array since backend now sends accumulated data
        // Backend handles the accumulation, frontend just displays the complete list
        setLiveSources(data.sources);
      }
    },
    [effectiveMessageId, messageId, sidebarThreadId]
  );

  // Update local state when props change (initial load)
  useEffect(() => {
    setLiveActivity(activity || []);
    setLiveSources(sources || []);
    setLiveStatus(status || "active");
  }, [effectiveMessageId, activity, sources, status]);

  // Listen for live deep research events
  useEffect(() => {
    if (!isActive || !effectiveMessageId) {
      return;
    }

    // Add event listener for deep research updates
    window.addEventListener("deepResearchUpdate", handleDeepResearchUpdate);

    // Cleanup function
    return () => {
      window.removeEventListener(
        "deepResearchUpdate",
        handleDeepResearchUpdate
      );
    };
  }, [isActive, effectiveMessageId, messageId, handleDeepResearchUpdate]);

  // Auto-scroll to latest activity when new activities are added
  useEffect(() => {
    if (activeTab === "activity" && liveActivity.length > 0) {
      const activityContainer = document.querySelector(
        "[data-activity-container]"
      );
      if (activityContainer) {
        setTimeout(() => {
          activityContainer.scrollTop = activityContainer.scrollHeight;
        }, 100);
      }
    }
  }, [liveActivity.length, activeTab]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (!isActive) {
    return null;
  }

  const getActivityIcon = (item) => {
    // Check if activity has a source URL - prioritize showing favicon
    const sourceUrl = item.sourceUrl || item.source?.url;
    if (sourceUrl) {
      const faviconUrl = getFaviconUrl(sourceUrl);
      return (
        <div className="flex-shrink-0 w-5 h-5 rounded bg-background border flex items-center justify-center overflow-hidden">
          {faviconUrl ? (
            <img
              src={faviconUrl}
              alt=""
              className="w-3 h-3"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <Globe
            className="w-3 h-3 text-muted-foreground"
            style={{ display: faviconUrl ? "none" : "flex" }}
          />
        </div>
      );
    }

    // Check if it's a search activity
    const isSearchActivity =
      item.message?.toLowerCase().includes("search") ||
      item.description?.toLowerCase().includes("search") ||
      item.activity?.toLowerCase().includes("search") ||
      item.type === "search";

    if (isSearchActivity) {
      return <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />;
    }

    return <ArtifactLogo className="h-5 w-5 flex-shrink-0" />;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    try {
      return new Date(timestamp).toLocaleTimeString();
    } catch {
      return "";
    }
  };

  const getFaviconUrl = (url) => {
    if (!url) return null;
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch {
      return null;
    }
  };

  const extractDomain = (url) => {
    if (!url) return "";
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return url;
    }
  };

  return (
    <div className="flex flex-col h-full w-full bg-background z-[9999] border-l border-border">
      {/* Header with Tabs and Close Button */}
      <div className="flex items-center justify-center p-4 border-b border-border relative z-[10000]">
        {/* Custom Rounded Tabs - Centered */}
        <div className="flex bg-muted/20 rounded-full p-1.5">
          <button
            onClick={() => setActiveTab("activity")}
            className={cn(
              "text-sm font-medium py-2.5 px-5 rounded-full transition-all duration-200 bg-transparent",
              activeTab === "activity"
                ? "bg-foreground text-background shadow-sm"
                : "bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30"
            )}
          >
            Activity
            {liveActivity.length > 0 && (
              <span className="ml-1 text-xs opacity-70">
                ({liveActivity.length})
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("sources")}
            className={cn(
              "text-sm font-medium py-2.5 px-5 rounded-full transition-all duration-200",
              activeTab === "sources"
                ? "bg-foreground text-background shadow-sm"
                : "bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30"
            )}
          >
            {liveSources.length} Sources
          </button>
        </div>

        {/* Close Button - Fixed Right */}
        <button
          onClick={onClose}
          className="absolute right-4 h-8 w-8 p-0 flex items-center justify-center transition-all bg-transparent border-none outline-none hover:bg-transparent group"
        >
          <X className="h-5 w-5 text-foreground group-hover:opacity-70 transition-opacity" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden p-4">
        {activeTab === "activity" ? (
          <div
            className="h-full overflow-y-auto space-y-2"
            data-activity-container
          >
            {liveActivity.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-500" />
                  <p>Starting research...</p>
                </div>
              </div>
            ) : (
              <AnimatePresence>
                {liveActivity.map((item, index) => (
                  <motion.div
                    key={`${index}-${item.timestamp || Date.now()}`}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{
                      duration: 0.4,
                      ease: "easeOut",
                      delay: index < 5 ? index * 0.1 : 0, // Only delay first few items
                    }}
                    className="flex items-start gap-3 py-2"
                  >
                    <div className="mt-1">{getActivityIcon(item)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base text-foreground leading-relaxed">
                        {item.message || item.description || item.activity}
                      </p>
                      {item.timestamp && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTimestamp(item.timestamp)}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        ) : (
          <div className="h-full overflow-y-auto">
            {/* Citations Header */}
            <div className="mb-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Citations
              </h3>
            </div>

            <div className="space-y-3">
              {liveSources.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-muted-foreground">
                  <div className="text-center">
                    {liveStatus === "active" ? (
                      <>
                        <Search className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                        <p>Gathering sources...</p>
                      </>
                    ) : (
                      <p>No sources found</p>
                    )}
                  </div>
                </div>
              ) : (
                <AnimatePresence>
                  {liveSources.map((source, index) => {
                    const faviconUrl = getFaviconUrl(source.url);
                    const domain = extractDomain(source.url);

                    return (
                      <motion.div
                        key={`${index}-${source.url || Date.now()}`}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{
                          duration: 0.4,
                          ease: "easeOut",
                          delay: index < 3 ? index * 0.15 : 0, // Only delay first few items
                        }}
                        className="p-4 rounded-xl bg-muted/20 hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={() =>
                          source.url && window.open(source.url, "_blank")
                        }
                      >
                        <div className="flex items-start gap-3">
                          {/* Favicon */}
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-background border flex items-center justify-center overflow-hidden">
                            {faviconUrl ? (
                              <img
                                src={faviconUrl}
                                alt=""
                                className="w-5 h-5"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <Globe
                              className="w-4 h-4 text-muted-foreground"
                              style={{ display: faviconUrl ? "none" : "flex" }}
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            {/* Title */}
                            <h4 className="text-sm font-medium text-foreground mb-1 line-clamp-2">
                              {source.title ||
                                source.name ||
                                `Source ${index + 1}`}
                            </h4>

                            {/* Domain */}
                            <p className="text-xs text-muted-foreground mb-2">
                              {domain}
                            </p>

                            {/* Excerpt */}
                            {source.excerpt && (
                              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
                                {source.excerpt}
                              </p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Status Footer */}
      <div className="border-t border-border p-4 bg-muted/5">
        <div className="flex items-start gap-3 text-sm">
          {liveStatus === "active" && (
            <>
              <ArtifactLogo className="h-6 w-6" />
              <AnimatedShinyText className="text-base font-medium">
                Thinking...
              </AnimatedShinyText>
            </>
          )}
          {liveStatus === "complete" && (
            <>
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-base text-muted-foreground">
                Research completed • {liveSources.length} sources
              </span>
            </>
          )}
          {liveStatus === "error" && (
            <>
              <AlertCircle className="h-4 w-4 text-red-500" />
              <span className="text-muted-foreground">Research failed</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

DeepResearch.propTypes = {
  isActive: PropTypes.bool,
  onClose: PropTypes.func,
  isLoading: PropTypes.bool,
  activity: PropTypes.array,
  sources: PropTypes.array,
  deepResearch: PropTypes.bool,
  status: PropTypes.string,
  messageId: PropTypes.string,
  originalQueryMessageId: PropTypes.string,
};

export default DeepResearch;
